import secureApiService from './secureApiService'
import { AnnouncementJSON, AnnouncementModel } from '@/models/announcement.model'
import { PaginatedResponse, normalizePaginationMeta } from '@/models/templates/pagination.model'

export interface AnnouncementQueryParams {
  page?: number
  perPage?: number
  courseId?: string
  programmeId?: string
  search?: string
  isPinned?: boolean
  isActive?: boolean
}

export interface AnnouncementMutationPayload {
  title: string
  content: string
  courseId?: string | null
  programmeId?: string | null
  isPinned?: boolean
  isActive?: boolean
  sendNotifications?: boolean
}

interface AnnouncementCollectionResponseJSON {
  success: boolean
  message?: string
  data?: AnnouncementJSON[] | AnnouncementJSON | null
  meta?: Record<string, unknown> | null
}

interface AnnouncementSingleResponseJSON {
  success: boolean
  message?: string
  data?: AnnouncementJSON | null
}

class AnnouncementsService {
  private basePath = '/announcements'

  private normalizeListResponse(
    payload: AnnouncementCollectionResponseJSON | undefined,
    fallbackMeta: { page: number; perPage: number },
  ): PaginatedResponse<AnnouncementModel> {
    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch announcements')
    }

    const rawData = Array.isArray(payload.data)
      ? payload.data
      : payload.data
        ? [payload.data]
        : []

    const data = rawData.map((announcement) => AnnouncementModel.fromJSON(announcement))
    const meta =
      normalizePaginationMeta(payload.meta) ??
      ({
        total: data.length,
        page: fallbackMeta.page,
        perPage: fallbackMeta.perPage,
      } as PaginatedResponse<AnnouncementModel>['meta'])

    return { data, meta }
  }

  private buildQuery(params: AnnouncementQueryParams = {}): string {
    const searchParams = new URLSearchParams()

    if (params.page) searchParams.append('page', String(params.page))
    if (params.perPage) searchParams.append('perPage', String(params.perPage))
    if (params.courseId) searchParams.append('courseId', params.courseId)
    if (params.programmeId) searchParams.append('programmeId', params.programmeId)
    if (params.search) searchParams.append('search', params.search)
    if (typeof params.isPinned === 'boolean') searchParams.append('isPinned', String(params.isPinned))
    if (typeof params.isActive === 'boolean') searchParams.append('isActive', String(params.isActive))

    return searchParams.toString()
  }

  async getAnnouncements(params: AnnouncementQueryParams = {}): Promise<PaginatedResponse<AnnouncementModel>> {
    const queryString = this.buildQuery(params)
    const endpoint = queryString ? `${this.basePath}?${queryString}` : this.basePath
    const response = await secureApiService.get<AnnouncementCollectionResponseJSON>(endpoint, { requireAuth: true })
    return this.normalizeListResponse(response.data, {
      page: params.page ?? 1,
      perPage: params.perPage ?? 25,
    })
  }

  async getLearnerAnnouncements(params: AnnouncementQueryParams = {}): Promise<PaginatedResponse<AnnouncementModel>> {
    const searchParams = new URLSearchParams()
    if (params.page) searchParams.append('page', String(params.page))
    if (params.perPage) searchParams.append('perPage', String(params.perPage))
    const queryString = searchParams.toString()
    const endpoint = queryString ? `${this.basePath}/me?${queryString}` : `${this.basePath}/me`
    const response = await secureApiService.get<AnnouncementCollectionResponseJSON>(endpoint, { requireAuth: true })
    return this.normalizeListResponse(response.data, {
      page: params.page ?? 1,
      perPage: params.perPage ?? 25,
    })
  }

  async getAnnouncementById(announcementId: string): Promise<AnnouncementModel> {
    const response = await secureApiService.get<AnnouncementSingleResponseJSON>(`${this.basePath}/${announcementId}`, {
      requireAuth: true,
    })
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Unable to fetch announcement')
    }
    return AnnouncementModel.fromJSON(response.data.data)
  }

  async createAnnouncement(payload: AnnouncementMutationPayload): Promise<AnnouncementModel> {
    const response = await secureApiService.post<AnnouncementSingleResponseJSON>(this.basePath, {
      ...payload,
      sendNotifications: payload.sendNotifications ?? true,
    })
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Unable to create announcement')
    }
    return AnnouncementModel.fromJSON(response.data.data)
  }

  async updateAnnouncement(announcementId: string, payload: AnnouncementMutationPayload): Promise<AnnouncementModel> {
    const response = await secureApiService.put<AnnouncementSingleResponseJSON>(`${this.basePath}/${announcementId}`, payload)
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Unable to update announcement')
    }
    return AnnouncementModel.fromJSON(response.data.data)
  }

  async deleteAnnouncement(announcementId: string): Promise<void> {
    const response = await secureApiService.delete<AnnouncementSingleResponseJSON>(`${this.basePath}/${announcementId}`)
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Unable to delete announcement')
    }
  }

  async setPinState(announcementId: string, action: 'pin' | 'unpin'): Promise<AnnouncementModel> {
    const response = await secureApiService.post<AnnouncementSingleResponseJSON>(`${this.basePath}/${announcementId}/${action}`)
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Unable to update announcement pin state')
    }
    return AnnouncementModel.fromJSON(response.data.data)
  }
}

export const announcementsService = new AnnouncementsService()
export default announcementsService
